import {
  DASHBOARD_CURRENCIES,
  DASHBOARD_RANGES,
  buildSelectedHistorySeries,
  formatChartLabel,
  formatNative,
  formatUsd,
  getAccountValuePresentation,
  getChartTitle,
  getChartValueFormatter,
} from './optionsDashboardUtils';
import { getDashboardAssetMeta } from './optionsAssetMetadata';
import { formatLocalizedDate, formatLocalizedNumber, formatLocalizedPercent } from './optionsLocaleFormatting';

export const PERFORMANCE_CURRENCIES = DASHBOARD_CURRENCIES;
export const PERFORMANCE_RANGES = DASHBOARD_RANGES;

const DEFAULT_PERFORMANCE_NOTES = {
  separation: 'Deposits, withdrawals and transfers affect account equity, but they are not counted as trading profit or loss.',
  usdReturn: 'Return is shown by capital period in BTC or ETH views. USD does not invent a consolidated return when the backend marks it as unreliable.',
  rangeReturnUnavailable: 'Return unavailable across multiple capital periods',
};

const buildPerformanceNotes = (t = null) => ({
  separation: t ? t('performance.tradingNote') : DEFAULT_PERFORMANCE_NOTES.separation,
  usdReturn: t ? t('performance.usdReturnNote') : DEFAULT_PERFORMANCE_NOTES.usdReturn,
  rangeReturnUnavailable: t ? t('performance.returnUnavailableMultiple') : DEFAULT_PERFORMANCE_NOTES.rangeReturnUnavailable,
});

const translateReturnReason = (reason, t = null) => {
  if (!reason) return reason;
  if (!t) return reason;
  if (reason === 'Return unavailable across multiple capital periods') return t('performance.returnUnavailableMultiple');
  if (reason === 'Return unavailable for this range') return t('performance.returnUnavailable');
  return reason;
};

const RETURN_REASON_CODES = {
  RANGE_BEFORE_RELIABLE_HISTORY: 'range_before_reliable_history',
  INSUFFICIENT_SNAPSHOTS: 'insufficient_snapshots',
  STARTING_EQUITY_MISSING: 'starting_equity_missing',
  STARTING_EQUITY_NON_POSITIVE: 'starting_equity_non_positive',
  CASH_FLOW_ADJUSTED_RETURN_UNAVAILABLE: 'cash_flow_adjusted_return_unavailable',
  NO_CAPITAL_PERIOD: 'no_capital_period',
  NO_VALID_PERIODS_IN_RANGE: 'no_valid_periods_in_range',
  INVALID_GROWTH_FACTOR: 'invalid_growth_factor',
  GENERIC_UNAVAILABLE: 'generic_unavailable',
};

const getReturnReasonCode = (reason = '') => {
  const normalized = String(reason || '').toLowerCase();
  if (!normalized) return RETURN_REASON_CODES.GENERIC_UNAVAILABLE;
  if (normalized.includes('histórico de equity insuficiente para ancorar o início e o fim') || normalized.includes('equity history insufficient to anchor')) {
    return RETURN_REASON_CODES.RANGE_BEFORE_RELIABLE_HISTORY;
  }
  if (normalized.includes('histórico de equity insuficiente para compor o retorno') || normalized.includes('snapshots')) {
    return RETURN_REASON_CODES.INSUFFICIENT_SNAPSHOTS;
  }
  if (normalized.includes('não foi possível obter starting/ending equity') || normalized.includes('equity inicial ou final indisponível')) {
    return RETURN_REASON_CODES.STARTING_EQUITY_MISSING;
  }
  if (normalized.includes('equity inicial não positiva')) {
    return RETURN_REASON_CODES.STARTING_EQUITY_NON_POSITIVE;
  }
  if (normalized.includes('cash flows externos detectados')) {
    return RETURN_REASON_CODES.CASH_FLOW_ADJUSTED_RETURN_UNAVAILABLE;
  }
  if (normalized.includes('não há períodos de capital válidos')) {
    return RETURN_REASON_CODES.NO_VALID_PERIODS_IN_RANGE;
  }
  if (normalized.includes('fator de crescimento')) {
    return RETURN_REASON_CODES.INVALID_GROWTH_FACTOR;
  }
  return RETURN_REASON_CODES.GENERIC_UNAVAILABLE;
};

const buildReturnUnavailableCopy = ({
  selectedCurrency,
  performanceData,
  rangeView,
  currentPeriod,
  t = null,
}) => {
  const reason = rangeView?.range_return_pct_reason
    || performanceData?.return_pct_reason
    || currentPeriod?.current_return_pct_reason
    || null;
  const reasonCode = currentPeriod || rangeView || performanceData ? getReturnReasonCode(reason) : RETURN_REASON_CODES.NO_CAPITAL_PERIOD;
  const startedAt = currentPeriod?.started_at || rangeView?.periods?.[0]?.started_at || null;
  const startedAtLabel = startedAt ? formatDateLong(startedAt) : null;
  const capitalEventType = currentPeriod?.started_after_cash_flow_type || rangeView?.current_period?.started_after_cash_flow_type || null;
  const capitalEventLabel = capitalEventType ? translateCashFlowType(capitalEventType, t) : null;

  let body = t ? t('performance.returnReasons.genericUnavailable') : 'There is not enough data to calculate a reliable return for this period.';
  let note = null;

  if (reasonCode === RETURN_REASON_CODES.RANGE_BEFORE_RELIABLE_HISTORY && startedAtLabel && capitalEventLabel) {
    body = formatMessage(
      t ? t('performance.returnReasons.rangeBeforeReliableHistoryAfterCashFlow') : 'This capital period started on {date} after a {event}. Part of the selected range happens before GENI has reliable capital history for this asset.',
      { date: startedAtLabel, event: capitalEventLabel.toLowerCase() }
    );
    note = t ? t('performance.returnReasons.cashFlowsSeparatedNote') : 'Deposits, withdrawals and transfers are not treated as profit or loss. After a capital change, GENI starts a new capital period to measure return correctly.';
  } else if (reasonCode === RETURN_REASON_CODES.RANGE_BEFORE_RELIABLE_HISTORY) {
    body = t ? t('performance.returnReasons.rangeBeforeReliableHistory') : 'Part of the selected range happens before the reliable capital history currently available in GENI.';
  } else if (reasonCode === RETURN_REASON_CODES.INSUFFICIENT_SNAPSHOTS) {
    body = t ? t('performance.returnReasons.insufficientSnapshots') : 'There are not enough equity snapshots yet to calculate return safely for this period.';
  } else if (reasonCode === RETURN_REASON_CODES.STARTING_EQUITY_MISSING) {
    body = t ? t('performance.returnReasons.missingStartingEquity') : 'GENI does not yet have a reliable starting equity for this range.';
  } else if (reasonCode === RETURN_REASON_CODES.STARTING_EQUITY_NON_POSITIVE) {
    body = t ? t('performance.returnReasons.nonPositiveStartingEquity') : 'The starting capital base for this range is not positive, so a percentage return would not be reliable.';
  } else if (reasonCode === RETURN_REASON_CODES.CASH_FLOW_ADJUSTED_RETURN_UNAVAILABLE) {
    body = t ? t('performance.returnReasons.cashFlowAdjustedUnavailable') : 'External cash flows were detected in the selected period and GENI did not confirm a reliable adjusted return for this window.';
    note = t ? t('performance.returnReasons.cashFlowsSeparatedNote') : 'Deposits, withdrawals and transfers are not treated as profit or loss. After a capital change, GENI starts a new capital period to measure return correctly.';
  } else if (reasonCode === RETURN_REASON_CODES.NO_VALID_PERIODS_IN_RANGE) {
    body = t ? t('performance.returnReasons.noValidPeriods') : 'There is no validated capital period inside the selected range yet.';
  } else if (reasonCode === RETURN_REASON_CODES.INVALID_GROWTH_FACTOR) {
    body = t ? t('performance.returnReasons.invalidGrowthFactor') : 'One of the capital-period segments produced a growth factor that is not reliable enough to display as return.';
  } else if (reasonCode === RETURN_REASON_CODES.NO_CAPITAL_PERIOD) {
    body = t ? t('performance.returnReasons.noCapitalPeriod') : 'GENI does not have a validated capital period for this asset yet.';
  }

  const title = t ? t('performance.returnUnavailableWhy') : "Why isn't return available?";
  const helper = t ? t('performance.returnNotAvailableYet') : 'Return not available yet';

  return {
    status: 'unavailable',
    reason,
    reasonCode,
    title,
    helper,
    body,
    note,
    startedAt,
    startedAtLabel,
    capitalEventType,
    capitalEventLabel,
    selectedCurrency,
  };
};

const normalizeZero = (value) => {
  if (value === null || value === undefined) return value;
  const numeric = Number(value);
  return Math.abs(numeric) < 1e-12 ? 0 : numeric;
};

const getSignedPrefix = (value) => {
  if (value > 0) return '+';
  if (value < 0) return '-';
  return '';
};

export const getValueTone = (value) => {
  if (value === null || value === undefined) return 'text-white';
  const numeric = normalizeZero(value);
  if (numeric > 0) return 'text-[#22C55E]';
  if (numeric < 0) return 'text-[#EF4444]';
  return 'text-white';
};

export const formatSignedUsd = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  const prefix = getSignedPrefix(numeric);
  return `${prefix}${formatUsd(Math.abs(numeric), digits)}`;
};

export const formatSignedNative = (value, currency, digits = 4) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  const prefix = getSignedPrefix(numeric);
  return `${prefix}${formatNative(Math.abs(numeric), currency, digits)}`;
};

export const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  const percentValue = normalizeZero(Number(value) * 100);
  if (percentValue === 0) return '0%';
  const absValue = Math.abs(percentValue);
  const digits = absValue >= 10 ? 1 : absValue >= 1 ? 2 : 4;
  return `${getSignedPrefix(percentValue)}${formatLocalizedPercent(absValue, digits)}`;
};

export const formatDateLong = (value) => {
  return formatLocalizedDate(value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTimeLong = (value) => {
  return formatLocalizedDate(value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const sanitizeTestId = (value) => String(value || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-');

export const getCurrencyDigits = (currency) => (currency === 'BTC' ? 4 : 2);

const hasMeaningfulNumber = (value) => value !== null && value !== undefined && !Number.isNaN(Number(value));

export const getPerformanceCurrencyBucket = (collection, selectedCurrency) => {
  const items = collection?.currencies || collection?.by_currency || [];
  if (selectedCurrency === 'usd') return null;
  return items.find((item) => item.currency === selectedCurrency.toUpperCase()) || null;
};

export const getCapitalPeriodView = (capitalPeriodsData, currency) => {
  return capitalPeriodsData?.currencies?.find((item) => item.currency === currency) || null;
};

export const getCurrentCapitalPeriodView = (currentCapitalPeriodsData, currency) => {
  return currentCapitalPeriodsData?.currencies?.find((item) => item.currency === currency) || null;
};

const getCurrentAssetUsdPrice = ({ accountData, performanceData, historyData, currency }) => {
  const code = String(currency || '').toUpperCase();
  const fromPerformance = (performanceData?.currencies || []).find((item) => item.currency === code)?.ending_usd_price;
  if (hasMeaningfulNumber(fromPerformance)) return Number(fromPerformance);

  const fromAccount = (accountData?.account_equity?.currencies || []).find((item) => item.currency === code)?.usd_price;
  if (hasMeaningfulNumber(fromAccount)) return Number(fromAccount);

  const fromHistory = [...(historyData?.items || [])].reverse().find((item) => item.currency === code && hasMeaningfulNumber(item.usd_price))?.usd_price;
  return hasMeaningfulNumber(fromHistory) ? Number(fromHistory) : null;
};

const convertUsdToNative = ({ usdValue, currency, accountData, performanceData, historyData }) => {
  if (usdValue === null || usdValue === undefined || currency === 'usd') return null;
  const usdPrice = getCurrentAssetUsdPrice({ accountData, performanceData, historyData, currency });
  if (!hasMeaningfulNumber(usdPrice)) return null;
  return Number(usdValue) / Number(usdPrice);
};

const formatCostUsd = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  if (numeric === 0) return formatUsd(0, digits);
  return `-${formatUsd(Math.abs(numeric), digits)}`;
};

const formatCostNative = (value, currency, digits = 4) => {
  if (value === null || value === undefined) return '—';
  const numeric = normalizeZero(value);
  const formatted = formatNative(Math.abs(numeric), currency, digits);
  if (numeric === 0) return formatted;
  return `-${formatted}`;
};

const formatMessage = (template, params = {}) => {
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value ?? '')),
    template || ''
  );
};

const buildTradingMetricInfo = ({ tradingCard, feesCard, t = null }) => ({
  title: t ? t('dashboard.tradingPnl') : 'Trading P&L',
  description: t ? t('performance.metricInfo.trading.description') : 'Net trading result inside the selected context.',
  sections: [
    {
      heading: t ? t('performance.metricInfo.sections.whatIs') : 'What it is',
      body: t ? t('performance.metricInfo.trading.whatIs') : 'Net trading result for the selected context, separated from deposits, withdrawals and transfers.',
    },
    {
      heading: t ? t('performance.metricInfo.sections.howCalculated') : 'How it is calculated',
      body: t ? t('performance.metricInfo.trading.howCalculated') : 'Trading P&L combines realized and unrealized trading results and subtracts the fees already associated with the selected operations.',
      formula: t ? t('performance.metricInfo.trading.formula') : 'Trading P&L = Realized + Unrealized - Fees',
      breakdown: [
        { label: t ? t('dashboard.realized') : 'Realized', value: tradingCard.realized },
        { label: t ? t('dashboard.unrealized') : 'Unrealized', value: tradingCard.unrealized },
        { label: t ? t('dashboard.fees') : 'Fees', value: feesCard.value },
      ],
      note: t ? t('performance.metricInfo.trading.note') : 'Deposits, withdrawals and transfers stay outside this calculation.',
    },
  ],
});

const buildSimpleMetricInfo = ({ title, description, body, note = null, value, secondary = null, t = null }) => ({
  title,
  description,
  sections: [
    {
      heading: t ? t('performance.metricInfo.sections.whatIs') : 'What it is',
      body,
      breakdown: [
        { label: t ? t('performance.metricInfo.currentValueLabel') : 'Current value', value, note: secondary || null },
      ],
      note,
    },
  ],
});

const buildAccountReturnInfo = ({ returnCard, t = null }) => {
  const raw = returnCard.raw || {};
  const title = t ? t('performance.returnLabel') : 'Return';
  if (returnCard.status === 'unavailable') {
    return {
      title,
      description: t ? t('performance.returnUnavailableWhy') : "Why isn't return available?",
      sections: [
        {
          heading: t ? t('performance.metricInfo.sections.whatIs') : 'What it is',
          body: returnCard.unavailable?.body,
          note: returnCard.unavailable?.note,
          breakdown: [
            { label: t ? t('performance.returnStatusLabel') : 'Status', value: t ? t('performance.returnNotAvailableYet') : 'Return not available yet' },
            { label: t ? t('performance.startingEquity') : 'Starting Equity', value: raw.starting_equity_usd !== null && raw.starting_equity_usd !== undefined ? formatUsd(raw.starting_equity_usd) : '—' },
            { label: t ? t('performance.endingEquity') : 'Ending Equity', value: raw.ending_equity_usd !== null && raw.ending_equity_usd !== undefined ? formatUsd(raw.ending_equity_usd) : '—' },
          ],
        },
      ],
    };
  }

  const hasCashFlow = (raw.external_cash_flow_event_count || 0) > 0;
  const description = t ? t('performance.metricInfo.return.description') : 'Consolidated account return across every asset held, keeping deposits, withdrawals and transfers outside trading performance.';
  const whatIs = t ? t('performance.metricInfo.return.whatIs') : 'Return shows the consolidated result of the selected interval across every asset held in the account, so internal conversions between assets do not create artificial profit or loss.';
  const howCalculated = hasCashFlow
    ? (t ? t('performance.metricInfo.return.howCalculatedCombined') : 'Deposits, withdrawals or transfers happened inside the selected window, so GENI compounds the segments between those capital events.')
    : (t ? t('performance.metricInfo.return.howCalculatedSingle') : 'GENI compares the consolidated account value in USD at the start and end of the selected window, across every asset held.');
  const note = t ? t('performance.metricInfo.return.note') : 'Deposits, withdrawals and transfers remain separated from trading performance.';

  return {
    title,
    description,
    sections: [
      {
        heading: t ? t('performance.metricInfo.sections.whatIs') : 'What it is',
        body: whatIs,
        breakdown: [
          { label: t ? t('performance.metricInfo.returnPctLabel') : 'Return', value: returnCard.value || (t ? t('performance.returnUnavailableLabel') : 'Return unavailable') },
          { label: t ? t('performance.startingEquity') : 'Starting Equity', value: raw.starting_equity_usd !== null && raw.starting_equity_usd !== undefined ? formatUsd(raw.starting_equity_usd) : '—' },
          { label: t ? t('performance.endingEquity') : 'Ending Equity', value: raw.ending_equity_usd !== null && raw.ending_equity_usd !== undefined ? formatUsd(raw.ending_equity_usd) : '—' },
        ],
      },
      {
        heading: t ? t('performance.metricInfo.sections.howCalculated') : 'How it is calculated',
        body: howCalculated,
        note,
      },
    ],
  };
};

export const buildPerformanceReturnInfoModel = ({ selectedCurrency, returnCard, t = null }) => {
  return buildAccountReturnInfo({ returnCard, t });
};

export const buildTradingCardModel = ({ performanceData, selectedCurrency, t = null }) => {
  const notes = buildPerformanceNotes(t);
  if (!performanceData) {
    return {
      headline: '—',
      headlineSecondary: null,
      realized: '—',
      unrealized: '—',
      toneClass: 'text-white',
      note: notes.separation,
    };
  }

  if (selectedCurrency === 'usd') {
    const netValue = performanceData.totals_usd?.net_pnl_usd;
    return {
      headline: formatSignedUsd(netValue),
      headlineSecondary: null,
      realized: formatSignedUsd(performanceData.totals_usd?.realized_pnl_usd),
      unrealized: formatSignedUsd(performanceData.totals_usd?.unrealized_pnl_usd),
      toneClass: getValueTone(netValue),
      note: notes.separation,
    };
  }

  const currencyCode = selectedCurrency.toUpperCase();
  const bucket = getPerformanceCurrencyBucket(performanceData, selectedCurrency);
  const digits = getCurrencyDigits(currencyCode);

  return {
    headline: formatSignedNative(bucket?.net_pnl_native, currencyCode, digits),
    headlineSecondary: bucket?.net_pnl_usd !== null && bucket?.net_pnl_usd !== undefined ? `≈ ${formatSignedUsd(bucket.net_pnl_usd)}` : null,
    realized: `${formatSignedNative(bucket?.realized_pnl_native, currencyCode, digits)}${bucket?.realized_pnl_usd !== null && bucket?.realized_pnl_usd !== undefined ? ` · ≈ ${formatSignedUsd(bucket.realized_pnl_usd)}` : ''}`,
    unrealized: `${formatSignedNative(bucket?.unrealized_pnl_native, currencyCode, digits)}${bucket?.unrealized_pnl_usd !== null && bucket?.unrealized_pnl_usd !== undefined ? ` · ≈ ${formatSignedUsd(bucket.unrealized_pnl_usd)}` : ''}`,
    toneClass: getValueTone(bucket?.net_pnl_native),
    note: notes.separation,
  };
};

export const buildFeesCardModel = ({ performanceData, selectedCurrency, t = null }) => {
  if (!performanceData) {
    return { value: '—', helper: null, toneClass: 'text-white' };
  }

  if (selectedCurrency === 'usd') {
    const feeRows = (performanceData.currencies || [])
      .filter((item) => item.fees_native !== null && item.fees_native !== undefined && Math.abs(Number(item.fees_native || 0)) > 1e-12)
      .map((item) => formatCostNative(item.fees_native, item.currency, getCurrencyDigits(item.currency)));
    return {
      value: formatCostUsd(performanceData.totals_usd?.fees_usd),
      helper: feeRows.length ? `≈ ${feeRows.join(' · ')}` : null,
      toneClass: 'text-white',
    };
  }

  const currencyCode = selectedCurrency.toUpperCase();
  const bucket = getPerformanceCurrencyBucket(performanceData, selectedCurrency);
  return {
    value: formatCostNative(bucket?.fees_native, currencyCode, getCurrencyDigits(currencyCode)),
    helper: bucket?.fees_usd !== null && bucket?.fees_usd !== undefined ? `≈ ${formatCostUsd(bucket.fees_usd)}` : (t ? t('performance.usdAuditNote') : 'USD equivalent unavailable'),
    toneClass: 'text-white',
  };
};

export const buildReturnCardModel = ({ selectedCurrency, performanceData, currentCapitalPeriodsData, capitalPeriodsData, t = null, selectedRange = null, customRange = null }) => {
  // Return is a single, consolidated account-level figure across every asset held
  // (BTC, ETH, USDC, etc). It is ALWAYS a percentage, regardless of the BTC/USD
  // visualization toggle - the toggle only changes the secondary monetary equivalent.
  const accountReturn = capitalPeriodsData?.account_return || null;

  // Helper: ms timestamp of the requested range's start (used to decide whether to show effectiveStartNote).
  const _rangeStartMs = () => {
    const now = Date.now();
    if (selectedRange === '30d') return now - 30 * 24 * 60 * 60 * 1000;
    if (selectedRange === '90d') return now - 90 * 24 * 60 * 60 * 1000;
    if (selectedRange === 'ytd') return new Date(new Date().getFullYear(), 0, 1).getTime();
    if (selectedRange === 'all') return 0;
    if (selectedRange === 'custom') {
      const s = customRange?.startDate;
      return s ? new Date(s + 'T00:00:00Z').getTime() : 0;
    }
    return null;
  };

  // Build note only when effective_start_at is more than 1 day later than the requested range start.
  const _effectiveStartNote = (ar) => {
    if (!ar || !ar.effective_start_ms || !ar.effective_start_at) return null;
    const rangeStartMs = _rangeStartMs();
    if (rangeStartMs === null) return null;
    if (Number(ar.effective_start_ms) <= rangeStartMs + 86400000) return null;
    const dateStr = formatDateLong(ar.effective_start_at);
    return t
      ? t('performance.effectiveStartNote').replace('{date}', dateStr)
      : `Return calculated since ${dateStr}`;
  };

  if (!accountReturn || accountReturn.return_pct === null || accountReturn.return_pct === undefined) {
    const syntheticRangeView = accountReturn ? {
      range_return_pct: accountReturn.return_pct,
      range_return_pct_reason: accountReturn.return_pct_reason,
      periods: [],
    } : null;
    const unavailable = buildReturnUnavailableCopy({
      selectedCurrency,
      performanceData,
      rangeView: syntheticRangeView,
      currentPeriod: null,
      t,
    });
    return {
      label: t ? t('performance.returnLabel') : 'Return',
      value: '—',
      helper: unavailable.helper,
      description: unavailable.body,
      toneClass: 'text-white',
      raw: accountReturn,
      status: 'unavailable',
      unavailable,
      effectiveStartNote: null,
    };
  }

  const pnlUsd = accountReturn.pnl_usd;
  return {
    label: t ? t('performance.returnLabel') : 'Return',
    value: formatPercent(accountReturn.return_pct),
    helper: pnlUsd !== null && pnlUsd !== undefined ? `≈ ${formatSignedUsd(pnlUsd)}` : null,
    description: accountReturn.external_cash_flow_event_count > 0
      ? (t ? t('performance.metricInfo.return.howCalculatedCombined') : 'Deposits, withdrawals or transfers happened inside the selected window, so GENI compounds the segments between those capital events.')
      : (t ? t('performance.liveCurrentPeriod') : 'Consolidated across every asset held in the account'),
    toneClass: getValueTone(accountReturn.return_pct),
    raw: accountReturn,
    status: 'available',
    effectiveStartNote: _effectiveStartNote(accountReturn),
  };
};

export const buildCashFlowLabel = (marker, t = null) => {
  if (!marker) return 'Cash flow';
  const action = marker.type === 'withdrawal'
    ? (t?.('performance.withdrawalLabel') || 'Withdrawal')
    : marker.type === 'transfer'
      ? (t?.('performance.transferLabel') || 'Transfer')
      : (t?.('performance.depositLabel') || 'Deposit');
  return `${action} ${formatSignedNative(marker.amount_native, marker.currency, getCurrencyDigits(marker.currency))}`;
};

export const translateCashFlowType = (eventType, t = null) => {
  if (!eventType) return '—';
  if (eventType === 'deposit') return t ? t('performance.depositLabel') : 'Deposit';
  if (eventType === 'withdrawal') return t ? t('performance.withdrawalLabel') : 'Withdrawal';
  if (eventType === 'transfer') return t ? t('performance.transferLabel') : 'Transfer';
  return eventType;
};

const hasNumericValue = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

const getSnapshotTimestampMs = (item) => {
  const direct = Number(item?.snapshot_timestamp_ms);
  if (Number.isFinite(direct)) return direct;
  const parsed = item?.snapshot_at ? new Date(item.snapshot_at).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const getMarkerTimestampMs = (marker) => {
  const direct = Number(marker?.timestamp_ms);
  if (Number.isFinite(direct)) return direct;
  const parsed = marker?.timestamp ? new Date(marker.timestamp).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const groupUsdHistorySeries = (items) => {
  const grouped = new Map();

  items.forEach((item) => {
    const snapshotTimestampMs = getSnapshotTimestampMs(item);
    if (!Number.isFinite(snapshotTimestampMs) || !hasNumericValue(item?.equity_usd)) return;

    const existing = grouped.get(snapshotTimestampMs) || {
      snapshot_at: item.snapshot_at,
      snapshot_timestamp_ms: snapshotTimestampMs,
      value: 0,
      sourceRows: [],
      markerEvents: [],
      markerSummaries: [],
    };

    existing.value = roundToEight(existing.value + Number(item.equity_usd));
    existing.sourceRows.push(item);
    existing.snapshot_at = existing.snapshot_at || item.snapshot_at;
    grouped.set(snapshotTimestampMs, existing);
  });

  return Array.from(grouped.values()).sort((a, b) => a.snapshot_timestamp_ms - b.snapshot_timestamp_ms);
};

const filterNativeHistorySeries = (items, currencyCode) => {
  return items
    .filter((item) => String(item?.currency || '').toUpperCase() === currencyCode)
    .map((item) => {
      const snapshotTimestampMs = getSnapshotTimestampMs(item);
      if (!Number.isFinite(snapshotTimestampMs) || !hasNumericValue(item?.equity_native)) return null;
      return {
        snapshot_at: item.snapshot_at,
        snapshot_timestamp_ms: snapshotTimestampMs,
        value: Number(item.equity_native),
        value_usd: hasNumericValue(item?.equity_usd) ? Number(item.equity_usd) : null,
        sourceRows: [item],
        markerEvents: [],
        markerSummaries: [],
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.snapshot_timestamp_ms - b.snapshot_timestamp_ms);
};

const buildHistorySeriesForCurrency = (items, selectedCurrency) => {
  if (selectedCurrency === 'usd') return groupUsdHistorySeries(items);
  return filterNativeHistorySeries(items, selectedCurrency.toUpperCase());
};

const buildVisibleMarkers = (capitalPeriodsData, selectedCurrency) => {
  const views = capitalPeriodsData?.currencies || [];
  const relevantViews = selectedCurrency === 'usd'
    ? views
    : views.filter((item) => item.currency === selectedCurrency.toUpperCase());

  return relevantViews
    .flatMap((item) => item.cash_flow_markers || [])
    .map((marker) => {
      const timestampMs = getMarkerTimestampMs(marker);
      if (!Number.isFinite(timestampMs)) return null;
      return {
        ...marker,
        timestamp_ms: timestampMs,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.timestamp_ms - b.timestamp_ms);
};

const attachExactMarkerEvents = (series, markers, t = null) => {
  if (!markers.length) return series;
  const markersByTimestamp = new Map();

  markers.forEach((marker) => {
    const summary = buildCashFlowLabel(marker, t);
    const existing = markersByTimestamp.get(marker.timestamp_ms) || [];
    existing.push({ ...marker, summary });
    markersByTimestamp.set(marker.timestamp_ms, existing);
  });

  return series.map((point) => {
    const markerEvents = markersByTimestamp.get(point.snapshot_timestamp_ms) || [];
    return {
      ...point,
      markerEvents,
      markerSummaries: markerEvents.map((event) => event.summary),
    };
  });
};

const resolveMarkerValue = (timestampMs, series) => {
  if (!series.length) return null;
  if (series.length === 1) return series[0].value;
  if (timestampMs <= series[0].snapshot_timestamp_ms) return series[0].value;
  if (timestampMs >= series[series.length - 1].snapshot_timestamp_ms) return series[series.length - 1].value;

  for (let index = 1; index < series.length; index += 1) {
    const previous = series[index - 1];
    const current = series[index];
    if (timestampMs === current.snapshot_timestamp_ms) return current.value;
    if (previous.snapshot_timestamp_ms <= timestampMs && timestampMs <= current.snapshot_timestamp_ms) {
      const span = current.snapshot_timestamp_ms - previous.snapshot_timestamp_ms;
      if (span <= 0) return current.value;
      const ratio = (timestampMs - previous.snapshot_timestamp_ms) / span;
      return previous.value + ((current.value - previous.value) * ratio);
    }
  }

  return series[series.length - 1].value;
};

const buildMarkerPoints = (series, markers) => {
  return markers
    .map((marker) => {
      const resolvedValue = resolveMarkerValue(marker.timestamp_ms, series);
      if (!hasNumericValue(resolvedValue)) return null;

      return {
        ...marker,
        snapshot_timestamp_ms: marker.timestamp_ms,
        value: Number(resolvedValue),
        isCashFlowMarker: true,
        markerEvents: [marker],
      };
    })
    .filter(Boolean);
};

const roundToEight = (value) => Number(Number(value).toFixed(8));

const buildYAxisDomain = (series) => {
  const values = series.map((point) => point.value).filter(hasNumericValue).map(Number);
  if (!values.length) return [0, 1];

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  if (minValue === maxValue) {
    const basePadding = Math.max(Math.abs(maxValue) * 0.04, maxValue === 0 ? 1 : 0.5);
    return [minValue - basePadding, maxValue + basePadding];
  }

  const spread = maxValue - minValue;
  const padding = Math.max(spread * 0.12, Math.abs(maxValue) * 0.015, Math.abs(minValue) * 0.015);
  return [minValue - padding, maxValue + padding];
};

const buildXAxisDomain = (historyData, series, markers) => {
  const rangeMeta = historyData?.range || {};
  const requestedRange = String(rangeMeta.requested_range || '').toLowerCase();
  const startTimestampMs = Number(rangeMeta.start_timestamp_ms);
  const endTimestampMs = Number(rangeMeta.end_timestamp_ms);

  if (
    requestedRange && requestedRange !== 'all'
    && Number.isFinite(startTimestampMs)
    && Number.isFinite(endTimestampMs)
    && endTimestampMs > startTimestampMs
  ) {
    return [startTimestampMs, endTimestampMs];
  }

  const timestamps = [
    ...series.map((point) => point.snapshot_timestamp_ms),
    ...markers.map((marker) => marker.timestamp_ms),
  ].filter(Number.isFinite);

  if (!timestamps.length) return [0, 1];
  if (timestamps.length === 1) {
    const point = timestamps[0];
    return [point - (12 * 60 * 60 * 1000), point + (12 * 60 * 60 * 1000)];
  }

  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  const padding = Math.max((maxTimestamp - minTimestamp) * 0.05, 60 * 60 * 1000);
  return [minTimestamp - padding, maxTimestamp + padding];
};

export const buildPerformanceChartModel = ({ historyData, capitalPeriodsData, selectedCurrency, t = null }) => {
  const rawSeries = buildHistorySeriesForCurrency(historyData?.items || [], selectedCurrency);
  const markers = buildVisibleMarkers(capitalPeriodsData, selectedCurrency);
  const series = attachExactMarkerEvents(rawSeries, markers, t);
  const markerPoints = buildMarkerPoints(series, markers);

  return {
    state: series.length === 0 ? 'empty' : series.length === 1 ? 'single' : 'multi',
    series,
    markerPoints,
    markerLegend: markers,
    xDomain: buildXAxisDomain(historyData, series, markers),
    yDomain: buildYAxisDomain(series),
  };
};

export const buildCurrentCapitalPeriodModel = ({ currentCapitalPeriodsData, selectedCurrency, t = null }) => {
  if (selectedCurrency === 'usd') {
    return {
      title: t ? t('performance.currentCapitalPeriod') : 'Current Capital Period',
      description: t ? t('performance.currentCapitalCardDescription') : 'This card uses the validated backend period directly, without recalculating anything in the frontend.',
      period: currentCapitalPeriodsData?.account || null,
      currency: 'USD',
      variant: 'usd',
    };
  }

  const currencyCode = selectedCurrency.toUpperCase();
  const view = getCurrentCapitalPeriodView(currentCapitalPeriodsData, currencyCode);
  return {
    title: `${currencyCode} ${t ? t('performance.currentCapitalPeriod') : 'Current Capital Period'}`,
    description: t ? t('performance.currentCapitalCardDescription') : 'This card uses the validated backend period directly, without recalculating anything in the frontend.',
    period: view?.current_period || null,
    currency: currencyCode,
    variant: 'native',
  };
};

export const buildCapitalPeriodSections = ({ capitalPeriodsData, currentCapitalPeriodsData, selectedCurrency, t = null }) => {
  const targetCurrencies = selectedCurrency === 'usd' ? ['BTC', 'ETH'] : [selectedCurrency.toUpperCase()];

  return targetCurrencies
    .map((currency) => {
      const rangeView = getCapitalPeriodView(capitalPeriodsData, currency);
      const currentView = getCurrentCapitalPeriodView(currentCapitalPeriodsData, currency);
      // Backend already hides currencies with no real equity/activity ever. In the
      // aggregate USD view we skip an asset entirely once the backend has no data for
      // it, instead of rendering an empty card. An explicit BTC/ETH selection still
      // shows the (possibly empty) card, since the user asked for it directly.
      if (selectedCurrency === 'usd' && !rangeView && !currentView) {
        return null;
      }
      const rangeReturnReasonText = translateReturnReason(rangeView?.range_return_pct_reason, t) || null;

      return {
        currency,
        periods: rangeView?.periods || [],
        currentPeriod: currentView?.current_period || rangeView?.current_period || null,
        rangeReturnText: rangeReturnReasonText,
        rangeCrossesMultiplePeriods: Boolean(rangeView?.range_crosses_multiple_periods),
        emptyMessage: t ? t('performance.noCapitalPeriodsInRange') : 'No capital period inside this time range yet.',
      };
    })
    .filter(Boolean);
};

export const buildEquityProjectionModel = ({ currentCapitalPeriodsData, selectedCurrency }) => {
  if (selectedCurrency === 'usd') {
    return {
      status: 'usd_unavailable',
      asset: 'USD',
      projected_points: [],
    };
  }

  const currencyCode = selectedCurrency.toUpperCase();
  const currentView = getCurrentCapitalPeriodView(currentCapitalPeriodsData, currencyCode);
  return currentView?.equity_projection || {
    status: 'insufficient_history',
    asset: currencyCode,
    projected_points: [],
  };
};

export const buildAccountChangesModel = ({ accountData, performanceData, selectedCurrency, selectedRange, t = null }) => {
  const notes = buildPerformanceNotes(t);
  const rangeLabel = selectedRange.toUpperCase();
  if (!accountData || !performanceData) {
    return { rows: [], note: notes.separation };
  }

  if (selectedCurrency === 'usd') {
    return {
      rows: [],
      note: `${notes.separation} ${t ? t('performance.usdAuditNote') : 'Switch to BTC or ETH to audit external cash flows inside each capital period.'}`,
    };
  }

  const currencyCode = selectedCurrency.toUpperCase();
  const bucket = getPerformanceCurrencyBucket(performanceData, selectedCurrency);
  const accountPresentation = getAccountValuePresentation({ accountData, selectedCurrency, t });

  const rows = [];
  if (bucket?.net_pnl_native !== null && bucket?.net_pnl_native !== undefined) {
    rows.push({
      key: 'trading-pnl',
      label: (t ? t('performance.tradingPnlRange') : 'Trading P&L ({range})').replace('{range}', rangeLabel),
      value: formatSignedNative(bucket.net_pnl_native, currencyCode, getCurrencyDigits(currencyCode)),
      secondary: bucket?.net_pnl_usd !== null && bucket?.net_pnl_usd !== undefined ? `≈ ${formatSignedUsd(bucket.net_pnl_usd)}` : null,
      toneClass: getValueTone(bucket.net_pnl_native),
    });
  }
  if ((bucket?.external_cash_flow_event_count || 0) > 0 && bucket?.external_cash_flow_native !== null && bucket?.external_cash_flow_native !== undefined) {
    rows.push({
      key: 'external-cash-flow',
      label: (t ? t('performance.externalCashFlowsRange') : 'External Cash Flows ({range})').replace('{range}', rangeLabel),
      value: formatSignedNative(bucket.external_cash_flow_native, currencyCode, getCurrencyDigits(currencyCode)),
      secondary: bucket?.external_cash_flow_usd !== null && bucket?.external_cash_flow_usd !== undefined ? `≈ ${formatSignedUsd(bucket.external_cash_flow_usd)}` : (t ? t('performance.trackedSeparately') : 'Tracked separately from trading'),
      toneClass: 'text-[#60A5FA]',
    });
  }
  rows.push({
    key: 'account-value',
    label: t ? t('performance.currentAccountValue') : 'Current Account Value',
    value: accountPresentation.headlineValue,
    secondary: accountPresentation.headlineSecondary,
    toneClass: 'text-white',
  });

  return {
    rows,
    note: notes.separation,
  };
};

export const buildPerformanceAccountSummaryModel = ({
  accountData,
  performanceData,
  capitalPeriodsData,
  currentCapitalPeriodsData,
  historyData,
  selectedCurrency,
  t = null,
  selectedRange = null,
  customRange = null,
}) => {
  const currencies = [...(accountData?.account_equity?.currencies || [])]
    .filter((item) => Math.abs(Number(item?.equity_native) || 0) > 1e-9 || Math.abs(Number(item?.equity_usd) || 0) > 1e-9)
    .sort((a, b) => Number(b?.equity_usd || 0) - Number(a?.equity_usd || 0));

  const totalUsd = accountData?.account_equity?.total_account_value_usd;
  const displayCurrency = selectedCurrency === 'usd' ? 'USD' : selectedCurrency.toUpperCase();
  const displayNativeTotal = convertUsdToNative({
    usdValue: totalUsd,
    currency: selectedCurrency,
    accountData,
    performanceData,
    historyData,
  });

  const totalValue = selectedCurrency === 'usd'
    ? formatUsd(totalUsd)
    : hasMeaningfulNumber(displayNativeTotal)
      ? formatNative(displayNativeTotal, displayCurrency, getCurrencyDigits(displayCurrency))
      : '—';

  const totalSecondary = selectedCurrency === 'usd' || !hasMeaningfulNumber(totalUsd)
    ? null
    : `≈ ${formatUsd(totalUsd)}`;

  const assets = currencies.map((item) => {
    const meta = getDashboardAssetMeta(item.currency);
    return {
      key: meta.code,
      currency: meta.code,
      name: meta.name,
      nativeDisplay: formatNative(item.equity_native, meta.code, meta.digits),
      usdDisplay: item.equity_usd !== null && item.equity_usd !== undefined ? formatUsd(item.equity_usd) : (t ? t('dashboard.usdValuationUnavailable') : 'USD valuation unavailable'),
      usdToneClass: item.equity_usd !== null && item.equity_usd !== undefined ? 'text-white/60' : 'text-[#FBBF24]',
    };
  });

  const valuationStatus = accountData?.account_equity?.total_account_value_status;
  const unresolvedCurrencies = accountData?.account_equity?.unresolved_currencies || [];
  const valuationNote = unresolvedCurrencies.length
    ? (valuationStatus === 'partial'
      ? (t ? t('dashboard.accountValuePartialNote') : 'Partial USD valuation · excluded from total: {currencies}').replace('{currencies}', unresolvedCurrencies.join(', '))
      : (t ? t('dashboard.accountValueUnavailableNote') : 'USD valuation unavailable · missing: {currencies}').replace('{currencies}', unresolvedCurrencies.join(', ')))
    : null;

  const tradingCard = buildTradingCardModel({ performanceData, selectedCurrency, t });
  const feesCard = buildFeesCardModel({ performanceData, selectedCurrency, t });
  const returnCard = buildReturnCardModel({ selectedCurrency, performanceData, currentCapitalPeriodsData, capitalPeriodsData, t, selectedRange, customRange });

  // Cálculo do Trading P&L Líquido (Realized - Fees)
  const btcPrice = getCurrentAssetUsdPrice({ accountData, performanceData, historyData, currency: 'BTC' });
  const realizedUsd = performanceData?.totals_usd?.realized_pnl_usd || 0;
  const feesUsd = performanceData?.totals_usd?.fees_usd || 0;
  const tradingPnlUsd = realizedUsd - feesUsd;
  
  const convertUsdToBtcLocal = (usdVal, btcVal) => {
    if (btcVal === null || btcVal === undefined || btcVal <= 0) return null;
    return usdVal / btcVal;
  };
  const tradingPnlBtc = convertUsdToBtcLocal(tradingPnlUsd, btcPrice);

  const tradingPnlHeadline = selectedCurrency === 'btc' && tradingPnlBtc !== null
    ? formatSignedNative(tradingPnlBtc, 'BTC', 4)
    : formatSignedUsd(tradingPnlUsd);

  const tradingPnlHeadlineSecondary = selectedCurrency === 'btc'
    ? `≈ ${formatSignedUsd(tradingPnlUsd)}`
    : (tradingPnlBtc !== null ? `≈ ${formatSignedNative(tradingPnlBtc, 'BTC', 4)}` : null);

  const tradingPnlToneClass = getValueTone(tradingPnlUsd);

  // 1. Determinar o Início Operacional de Options com base no Capital Periods V2
  let earliestExposureMs = null;
  const datasToSearch = [capitalPeriodsData, currentCapitalPeriodsData].filter(Boolean);
  datasToSearch.forEach((periodsObj) => {
    (periodsObj?.currencies || []).forEach((curr) => {
      const currPeriod = curr.current_period;
      const allPeriodsInCurrency = [...(curr.periods || [])];
      if (currPeriod && !allPeriodsInCurrency.some(p => p.id === currPeriod.id)) {
        allPeriodsInCurrency.push(currPeriod);
      }
      allPeriodsInCurrency.forEach((p) => {
        const isV2Legit = p.v2_activation_type !== null && p.v2_activation_type !== undefined;
        if (isV2Legit) {
          const ms = new Date(p.started_at).getTime();
          if (earliestExposureMs === null || ms < earliestExposureMs) {
            earliestExposureMs = ms;
          }
        }
      });
    });
  });

  // Somar starting_equity_usd de todos os períodos V2 que começaram nesse início operacional (limiar de 24h para multi-asset)
  let initialEquityUsd = 0;
  let hasOperationalStart = false;
  const countedPeriodIds = new Set();

  if (earliestExposureMs !== null) {
    datasToSearch.forEach((periodsObj) => {
      (periodsObj?.currencies || []).forEach((curr) => {
        const currPeriod = curr.current_period;
        const allPeriodsInCurrency = [...(curr.periods || [])];
        if (currPeriod && !allPeriodsInCurrency.some(p => p.id === currPeriod.id)) {
          allPeriodsInCurrency.push(currPeriod);
        }
        allPeriodsInCurrency.forEach((p) => {
          const isV2Legit = p.v2_activation_type !== null && p.v2_activation_type !== undefined;
          if (isV2Legit && !countedPeriodIds.has(p.id)) {
            const ms = new Date(p.started_at).getTime();
            if (Math.abs(ms - earliestExposureMs) <= 24 * 60 * 60 * 1000) {
              const eq = p.starting_equity_usd || p.combined_equity?.initial_combined_usd || 0;
              initialEquityUsd += Number(eq);
              countedPeriodIds.add(p.id);
              hasOperationalStart = true;
            }
          }
        });
      });
    });
  }

  // 2. Determinar o rangeStartMs solicitado
  const getRangeStartMs = () => {
    const now = Date.now();
    if (selectedRange === '30d') return now - 30 * 24 * 60 * 60 * 1000;
    if (selectedRange === '90d') return now - 90 * 24 * 60 * 60 * 1000;
    if (selectedRange === 'ytd') return new Date(new Date().getFullYear(), 0, 1).getTime();
    if (selectedRange === 'all') return 0;
    if (selectedRange === 'custom') {
      const s = customRange?.startDate;
      return s ? new Date(s + 'T00:00:00Z').getTime() : 0;
    }
    return 0;
  };
  const rangeStartMs = getRangeStartMs();

  // 3. Aplicar as regras para determinar o capital inicial e effective start
  const backendStartingEquityUsd = capitalPeriodsData?.account_return?.starting_equity_usd ?? null;
  const backendEffectiveStartAt = capitalPeriodsData?.account_return?.effective_start_at ?? null;
  const backendEffectiveStartMs = backendEffectiveStartAt ? new Date(backendEffectiveStartAt).getTime() : 0;

  let validStartingCapitalUsd = backendStartingEquityUsd;
  let validEffectiveStartAt = backendEffectiveStartAt;
  let isOperationalLimited = false;

  if (hasOperationalStart && earliestExposureMs !== null) {
    const isYtdPreOperational = selectedRange === 'ytd' && earliestExposureMs > rangeStartMs;
    const isRangePreOperational = rangeStartMs <= earliestExposureMs;
    const isBackendPreOperational = backendEffectiveStartMs < earliestExposureMs && (selectedRange === 'all' || selectedRange === 'ytd' || rangeStartMs <= earliestExposureMs);

    if (selectedRange === 'all' || isYtdPreOperational || isRangePreOperational || isBackendPreOperational) {
      // Regra: se o range ou a baseline do backend alcançar período anterior ao primeiro trade, limita ao início operacional
      validStartingCapitalUsd = initialEquityUsd;
      validEffectiveStartAt = earliestExposureMs;
      isOperationalLimited = true;
    } else {
      validStartingCapitalUsd = backendStartingEquityUsd;
      validEffectiveStartAt = backendEffectiveStartAt;
    }
  }

  // 4. Calcular a porcentagem do Resultado / Capital Inicial
  let resultOnStartingCapitalPct = null;
  let resultOnStartingCapitalFormatted = '—';
  let resultOnStartingCapitalToneClass = 'text-white';

  if (validStartingCapitalUsd !== null && validStartingCapitalUsd > 0) {
    resultOnStartingCapitalPct = (tradingPnlUsd / validStartingCapitalUsd);
    resultOnStartingCapitalFormatted = formatPercent(resultOnStartingCapitalPct);
    resultOnStartingCapitalToneClass = getValueTone(resultOnStartingCapitalPct);
  }

  const usdBucket = performanceData?.totals_usd || null;
  const nativeBucket = selectedCurrency === 'usd' ? null : getPerformanceCurrencyBucket(performanceData, selectedCurrency);
  const realizedTone = getValueTone(selectedCurrency === 'usd' ? usdBucket?.realized_pnl_usd : nativeBucket?.realized_pnl_native);
  const unrealizedTone = getValueTone(selectedCurrency === 'usd' ? usdBucket?.unrealized_pnl_usd : nativeBucket?.unrealized_pnl_native);
  const infoMap = {
    trading: buildTradingMetricInfo({ tradingCard, feesCard, t }),
    realized: buildSimpleMetricInfo({
      title: t ? t('dashboard.realized') : 'Realized',
      description: t ? t('performance.metricInfo.realized.description') : 'P&L of positions already closed inside the selected context.',
      body: t ? t('performance.metricInfo.realized.whatIs') : 'P&L of operations already closed inside the selected context.',
      value: tradingCard.realized,
      t,
    }),
    unrealized: buildSimpleMetricInfo({
      title: t ? t('dashboard.unrealized') : 'Unrealized',
      description: t ? t('performance.metricInfo.unrealized.description') : 'Current P&L of positions that remain open.',
      body: t ? t('performance.metricInfo.unrealized.whatIs') : 'Current P&L of positions that remain open. This value can move while positions stay open.',
      note: t ? t('performance.metricInfo.unrealized.note') : 'This value can change while the positions remain open.',
      value: tradingCard.unrealized,
      t,
    }),
    fees: buildSimpleMetricInfo({
      title: t ? t('dashboard.fees') : 'Fees',
      description: t ? t('performance.metricInfo.fees.description') : 'Trading fees associated with the selected operations.',
      body: t ? t('performance.metricInfo.fees.whatIs') : 'Trading fees associated with the operations included in the selected range.',
      note: t ? t('performance.metricInfo.fees.note') : 'Fees are costs and remain treated inside GENI\'s validated P&L methodology.',
      value: feesCard.value,
      secondary: feesCard.helper,
      t,
    }),
    return: buildPerformanceReturnInfoModel({ selectedCurrency, returnCard, t }),
  };

  return {
    totalLabel: t ? t('dashboard.accountValueHeadline') : 'Total Account Value',
    totalValue,
    totalSecondary,
    valuationNote,
    assetsLabel: t ? t('performance.assetsCompactLabel') : 'Assets',
    assets,
    tradingLabel: t ? t('dashboard.tradingPnl') : 'Trading P&L',
    tradingValue: tradingPnlHeadline,
    tradingSecondary: tradingPnlHeadlineSecondary,
    tradingToneClass: tradingPnlToneClass,
    tradingInfo: infoMap.trading,
    realizedLabel: t ? t('dashboard.realized') : 'Realized',
    realizedValue: tradingCard.realized,
    realizedToneClass: realizedTone,
    realizedInfo: infoMap.realized,
    unrealizedLabel: t ? t('dashboard.unrealized') : 'Unrealized',
    unrealizedValue: tradingCard.unrealized,
    unrealizedToneClass: unrealizedTone,
    unrealizedInfo: infoMap.unrealized,
    feesLabel: t ? t('dashboard.fees') : 'Fees',
    feesValue: feesCard.value,
    feesSecondary: feesCard.helper,
    feesInfo: infoMap.fees,
    returnLabel: returnCard.label || (t ? t('performance.returnLabel') : 'Return'),
    returnValue: returnCard.value || '—',
    returnToneClass: returnCard.status === 'available' ? returnCard.toneClass : 'text-white',
    returnHelper: returnCard.status === 'unavailable' ? returnCard.helper : (selectedCurrency === 'usd' ? returnCard.helper : null),
    returnInfo: infoMap.return,
    returnEffectiveStartNote: returnCard.effectiveStartNote || null,
    
    // Novas métricas padronizadas de capital e líquido
    resultOnStartingCapitalFormatted,
    resultOnStartingCapitalToneClass,
    isOperationalLimited,
    earliestExposureMs,
  };
};

export {
  formatChartLabel,
  formatNative,
  formatUsd,
  getAccountValuePresentation,
  getChartTitle,
  getChartValueFormatter,
};

// ─── Capital Summary / Movements builders ────────────────────────────────────

const _formatMovementType = (type, t) => {
  if (!type) return '';
  const lower = String(type).toLowerCase();
  if (lower === 'deposit') return t ? t('performance.movementDeposit') : 'Deposit';
  if (lower === 'withdrawal') return t ? t('performance.movementWithdrawal') : 'Withdrawal';
  if (lower === 'transfer') return t ? t('performance.movementTransfer') : 'Transfer';
  return type;
};

const _signedNative = (amount, currency) => {
  if (amount === null || amount === undefined) return '';
  const prefix = Number(amount) >= 0 ? '+' : '';
  return `${prefix}${formatNative(amount, currency, 4)}`;
};

const _usdApprox = (amountUsd) => {
  if (amountUsd === null || amountUsd === undefined) return null;
  return `≈ ${formatUsd(Math.abs(Number(amountUsd)), 0)}`;
};

/**
 * Extracts ALL capital movements (deposits/withdrawals/transfers) from:
 * 1. cash_flow_markers in currencies (when present — production)
 * 2. period boundary cash flows (always present)
 * Deduplicates by period index + currency.
 */
export const buildCapitalMovementsModel = ({ capitalPeriodsAllData, t = null }) => {
  if (!capitalPeriodsAllData) return { movements: [], empty: true };

  const seen = new Set();
  const movements = [];

  for (const currView of (capitalPeriodsAllData.currencies || [])) {
    const currency = currView.currency;

    // Primary: cash_flow_markers (more precise – from options_cash_flows)
    const markers = currView.cash_flow_markers || [];
    for (const m of markers) {
      const key = `${m.timestamp_ms || m.timestamp}-${currency}-${m.type}`;
      if (seen.has(key)) continue;
      seen.add(key);
      movements.push({
        timestamp: m.timestamp || m.timestamp_ms,
        timestamp_ms: m.timestamp_ms || 0,
        currency,
        type: m.type,
        typeLabel: _formatMovementType(m.type, t),
        amountNative: m.amount_native,
        amountUsd: m.amount_usd,
        amountNativeFormatted: _signedNative(m.amount_native, currency),
        amountUsdFormatted: _usdApprox(m.amount_usd),
        source: 'marker',
      });
    }

    // Fallback: period boundary cash flows (when markers collection is empty)
    if (markers.length === 0) {
      for (const period of (currView.periods || [])) {
        if (!period.started_after_cash_flow_type) continue;
        const ts = period.started_timestamp_ms || (period.started_at ? new Date(period.started_at).getTime() : 0);
        const key = `${ts}-${currency}-${period.started_after_cash_flow_type}`;
        if (seen.has(key)) continue;
        seen.add(key);
        movements.push({
          timestamp: period.started_at,
          timestamp_ms: ts,
          currency,
          type: period.started_after_cash_flow_type,
          typeLabel: _formatMovementType(period.started_after_cash_flow_type, t),
          amountNative: period.started_after_cash_flow_amount_native,
          amountUsd: period.started_after_cash_flow_amount_usd || null,
          amountNativeFormatted: _signedNative(period.started_after_cash_flow_amount_native, currency),
          amountUsdFormatted: _usdApprox(period.started_after_cash_flow_amount_usd),
          source: 'period_boundary',
        });
      }
    }
  }

  movements.sort((a, b) => a.timestamp_ms - b.timestamp_ms);
  return { movements, empty: movements.length === 0 };
};

/**
 * Builds the capital summary (totals, cumulative returns).
 * Source of truth:
 *   - BTC cumulative return: capitalPeriodsAllData.currencies[BTC].twr_pct
 *   - USD cumulative return: capitalPeriodsAllData.account_return.return_pct
 */
export const buildCapitalSummaryModel = ({ capitalPeriodsAllData, accountData, selectedCurrency, t = null }) => {
  if (!capitalPeriodsAllData) return null;

  const currencies = capitalPeriodsAllData.currencies || [];

  let depositCount = 0;
  let withdrawalCount = 0;
  const depositTotals = {};
  const withdrawalTotals = {};

  // Count from ALL period boundaries across all currencies
  for (const currView of currencies) {
    const currency = currView.currency;
    const markers = currView.cash_flow_markers || [];
    const source = markers.length > 0 ? markers : (currView.periods || []).map((p) => ({
      type: p.started_after_cash_flow_type,
      amount_native: p.started_after_cash_flow_amount_native,
    })).filter((m) => m.type);

    for (const m of source) {
      const lower = String(m.type || '').toLowerCase();
      const amt = Number(m.amount_native ?? 0);
      if (lower === 'deposit' || (lower === 'transfer' && amt > 0)) {
        depositCount += 1;
        depositTotals[currency] = (depositTotals[currency] || 0) + Math.abs(amt);
      } else if (lower === 'withdrawal' || (lower === 'transfer' && amt < 0)) {
        withdrawalCount += 1;
        withdrawalTotals[currency] = (withdrawalTotals[currency] || 0) + Math.abs(amt);
      }
    }
  }

  // BTC cumulative return (twr_pct from range=all)
  const btcView = currencies.find((c) => c.currency === 'BTC');
  const btcTwr = btcView?.twr_pct ?? null;
  const btcTwrFormatted = btcTwr !== null ? formatPercent(btcTwr) : null;
  const btcTwrTone = btcTwr !== null ? getValueTone(btcTwr) : 'text-white';

  // USD cumulative return (consolidated account_return)
  const ar = capitalPeriodsAllData.account_return || {};
  const usdReturn = ar.return_pct ?? null;
  const usdReturnFormatted = usdReturn !== null ? formatPercent(usdReturn) : null;
  const usdReturnTone = usdReturn !== null ? getValueTone(usdReturn) : 'text-white';

  // Current equity (from account data)
  const currentEquityUsd = accountData?.account_value_usd ?? null;
  const currentEquityFormatted = currentEquityUsd !== null ? formatUsd(currentEquityUsd, 0) : null;

  // Primary deposit/withdrawal display currency
  const primaryCurrency = selectedCurrency && selectedCurrency !== 'usd' ? selectedCurrency.toUpperCase() : 'BTC';
  const depositTotal = depositTotals[primaryCurrency] ?? null;
  const withdrawalTotal = withdrawalTotals[primaryCurrency] ?? null;

  return {
    depositCount,
    depositCountLabel: t
      ? t('performance.depositsCountLabel').replace('{n}', depositCount)
      : `${depositCount} deposit(s)`,
    depositTotalFormatted: depositTotal !== null ? formatNative(depositTotal, primaryCurrency, 4) : null,
    depositTotals,
    withdrawalCount,
    withdrawalCountLabel: t
      ? t('performance.withdrawalsCountLabel').replace('{n}', withdrawalCount)
      : `${withdrawalCount} withdrawal(s)`,
    withdrawalTotalFormatted: withdrawalTotal !== null ? formatNative(withdrawalTotal, primaryCurrency, 4) : null,
    withdrawalTotals,
    currentEquityFormatted,
    btcTwrFormatted,
    btcTwrTone,
    usdReturnFormatted,
    usdReturnTone,
    primaryCurrency,
    titles: {
      capitalSummary: t ? t('performance.capitalSummaryTitle') : 'Capital Summary',
      capitalMovements: t ? t('performance.capitalMovementsTitle') : 'Capital Movements',
      totalDeposited: t ? t('performance.totalDepositedLabel') : 'Total deposited',
      totalWithdrawn: t ? t('performance.totalWithdrawnLabel') : 'Total withdrawn',
      currentEquity: t ? t('performance.currentEquityLabel') : 'Current equity',
      btcCumulativeReturn: t ? t('performance.btcCumulativeReturnLabel') : 'BTC cumulative return',
      usdCumulativeReturn: t ? t('performance.usdCumulativeReturnLabel') : 'USD cumulative return',
      noCashFlows: t ? t('performance.noCashFlows') : 'No capital movements recorded yet.',
    },
  };
};